"use client";

import { useEffect, useRef, useState } from "react";
import { Character, User } from "schema";

import Loading from "~/app/_components/Loading";
import PlayBack from "~/components/PlayBack";
import RecordLineModal from "~/components/RecordLineModal";
import ArchivePlayModal from "./_components/ArchivePlayModal";
import CopyPlayModal from "./_components/CopyPlayModal";
import CreateLineModal from "./_components/CreateLineModal";
import EditPlayModal from "./_components/EditPlayModal";
import LineDropDown from "./_components/LineDropDown";
import { Act, LineBucket } from "./page";

export default function ClientPage({
  playData,
  characters,
  stageHands,
  lineBuckets,
  friends,
  user,
  acts,
}: {
  playData: {
    id: number;
    resourceId: string | null;
    title: string;
    userId: string;
    name: string | null;
    handle: string | null;
    archived: boolean | null;
  };
  characters: Character[];
  stageHands: Character[];
  lineBuckets: LineBucket[];
  acts: Act[];
  friends: {
    id: string;
    handle: string | null;
    name: string | null;
    avatar: {
      colors: number;
      name: string;
      variant: "beam" | "marble" | "pixel" | "sunset" | "ring" | "bauhaus";
    } | null;
  }[];
  user: User;
}) {
  // const [lineBucketState, setLineBucketState] = useState(lineBuckets);
  const [isLoading, setIsLoading] = useState(false);
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const userCastedAs = characters
    .filter((item) => item.userId === user.id)
    .map((item) => item.id);

  // const lineRefs = useRef<(HTMLLIElement | null)[]>([]);
  const audioRef = useRef<HTMLAudioElement>(null);

  const linesWithAudio = lineBuckets.filter((item) => item.audioURL !== null);

  const playAudio = (lineId: number) => {
    if (currentId !== null && audioRef.current) {
      // Pause the currently playing audio
      audioRef.current.pause();
    }

    const index = linesWithAudio.findIndex((item) => item.lineId === lineId);
    if (index !== -1) {
      setCurrentIndex(index);
      setCurrentId(linesWithAudio[index].lineId);
      if (audioRef.current) {
        audioRef.current.src = linesWithAudio[index].audioURL!;
        void audioRef.current.play();
      }

      // if (lineRefs?.current?.[index]) {
      //   lineRefs.current[index]?.scrollIntoView({ behavior: "smooth" });
      // }
    }
  };

  const handleAudioEnd = () => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < linesWithAudio.length) {
      playAudio(linesWithAudio[nextIndex].lineId);
    } else {
      setCurrentId(null);
    }
  };

  useEffect(() => {
    const audioElement = audioRef.current;

    if (audioElement) {
      audioElement.addEventListener("ended", handleAudioEnd);
    }

    return () => {
      if (audioElement) {
        audioElement.removeEventListener("ended", handleAudioEnd);
      }
    };
  });

  return (
    <>
      <audio ref={audioRef} />
      <div className="container mx-auto sm:px-6 lg:px-8">
        <div className="items-start justify-between border-b py-4 md:flex">
          <div className="w-full">
            <div className="flex w-full items-center justify-between text-2xl font-bold text-gray-800">
              {playData.title}

              <div className="flex gap-2">
                <button
                  className="flex gap-1 p-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                  onClick={() => playAudio(linesWithAudio[0].lineId)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="size-5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z"
                    />
                  </svg>
                  Listen
                </button>
                {playData.userId === user.id ? (
                  <>
                    <CopyPlayModal
                      playInfo={{
                        id: playData.id,
                        author: playData.userId,
                        title: playData.title,
                        characters: characters,
                      }}
                      acts={acts}
                      setLoading={setIsLoading}
                    />
                    <EditPlayModal
                      users={friends}
                      play={playData}
                      characters={characters.map((item) => ({
                        id: item.id,
                        userId: item.userId,
                        name: item.name,
                        playId: item.playId,
                        email: "",
                      }))}
                    />
                    <ArchivePlayModal play={playData} />
                  </>
                ) : null}
              </div>
            </div>

            <p className="mt-2 text-gray-600">
              {playData.handle ? `@${playData.handle}` : playData.name}
            </p>
          </div>
        </div>
        <div className="mt-6 min-h-screen w-full bg-white">
          <section className="mx-auto w-full px-4 md:px-8">
            <ul className="w-full space-y-6">
              {lineBuckets.map((item, idx) => (
                <li
                  id={idx.toString()}
                  key={item.bucketId}
                  className="w-full rounded-md bg-white p-5"
                  // ref={(el) => (lineRefs.current[idx] = el)}
                >
                  <div>
                    <div>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center"></div>
                          {(() => {
                            if (item.characterName === "Act") {
                              return (
                                <h3 className="text-center text-4xl font-extrabold text-gray-800">
                                  {item.lineText}
                                </h3>
                              );
                            } else if (item.characterName === "Scene") {
                              return (
                                <h2 className="text-center text-2xl font-extrabold text-gray-800">
                                  {item.lineText}
                                </h2>
                              );
                            } else if (
                              item.characterName === "Stage Direction"
                            ) {
                              return (
                                <p className="ml-4 mt-2 italic text-gray-500">
                                  {item.lineText}
                                </p>
                              );
                            } else {
                              return (
                                <>
                                  <div className="flex items-center gap-3 align-middle">
                                    <h3
                                      className={
                                        userCastedAs.includes(item.characterId)
                                          ? "text-xl font-bold text-rose-800"
                                          : "text-xl font-bold text-gray-800"
                                      }
                                    >
                                      {item.characterName}
                                    </h3>
                                    {item.versions.length > 1 ? (
                                      <span className="whitespace-nowrap rounded-md bg-amber-100 px-2.5 py-0.5 text-sm font-bold text-amber-700">
                                        {item.versions.length} versions
                                      </span>
                                    ) : null}
                                  </div>
                                  <div className="my-2 flex items-center gap-2">
                                    {item.tags
                                      ? item.tags.map((item, idx) => (
                                          <span
                                            key={idx}
                                            className={`whitespace-nowrap rounded-full ${item.color === "red" ? "bg-red-100 text-red-700" : ""} ${item.color === "blue" ? "bg-blue-100 text-blue-700" : ""} ${item.color === "green" ? "bg-green-100 text-green-700" : ""} ${item.color === "amber" ? "bg-amber-100 text-amber-700" : ""} px-2.5 py-0.5 text-sm`}
                                          >
                                            {item.text}
                                          </span>
                                        ))
                                      : null}
                                  </div>
                                  <PlayBack
                                    id={item.lineId}
                                    audioRef={audioRef}
                                    isPlaying={currentId === item.lineId}
                                    onPlay={() => playAudio(item.lineId)}
                                    hasAudio={item.audioURL ? true : false}
                                  />
                                  <p
                                    dangerouslySetInnerHTML={{
                                      __html: item.lineText.replace(
                                        /\n/g,
                                        "<br />",
                                      ),
                                    }}
                                    className={`ml-4 mt-2 ${currentId === item.lineId ? "font-semibold text-rose-500" : "text-gray-500"}`}
                                  ></p>
                                </>
                              );
                            }
                          })()}
                          {/* <p className="ml-4 mt-2 text-gray-500">
                                {item.lineText}
                              </p> */}
                        </div>
                        <div className="mt-5 space-y-4 text-sm sm:mt-0 sm:space-y-2">
                          <span className="flex items-center text-gray-500">
                            {user.id === playData.userId ? (
                              <LineDropDown
                                characters={characters}
                                lineBucket={item}
                                stageHands={stageHands}
                                userCastList={userCastedAs}
                              />
                            ) : userCastedAs.includes(item.characterId) ? (
                              (() => {
                                const {
                                  lineId: id,
                                  playId,
                                  lineText: text,
                                  characterId,
                                  created_at,
                                  id: bucket,
                                } = item;

                                const line = {
                                  id,
                                  bucket,
                                  playId,
                                  text,
                                  characterId,
                                  created_at,
                                };

                                return <RecordLineModal line={line} />;
                              })()
                            ) : null}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            {user.id === playData.userId ? (
              <CreateLineModal
                tail={
                  lineBuckets.length !== 0
                    ? lineBuckets[lineBuckets.length - 1].bucketId
                    : undefined
                }
                characters={characters}
                stageHands={stageHands}
              />
            ) : null}
          </section>
        </div>
      </div>

      {isLoading ? <Loading /> : null}
    </>
  );
}
